import type { _0 } from '#tailwind-config/theme/backdropBlur';
import { type ClientInfo, type ExternalAssistantSettings, type SuggestedItem, type ThemeSettings } from 'consulinkai.typescript';

const $window = (<any>window);
const errorPrefix = 'Consulink-AI.Widget returns the following error: ';

let showChatBtn: boolean | null;
interface ICslWidget {
    init: (openDelay: number) => void;
}

class CslWidget implements ICslWidget {
    private guid: string;
    private appUrl: string;
    private apiUrl: string;
    private language: string;
    private assistant: ExternalAssistantSettings | null;
    private isOpen: boolean = false;
    private isExpanded: boolean = false;
    private chatButtonFrame: HTMLIFrameElement | null = null;
    private chatButton: HTMLElement | null = null;
    private chatWindow: HTMLElement | null = null;
    private chatWindowFrame: HTMLIFrameElement | null = null;
    private extWindow: Window | null = null;
    private clientInfo: ClientInfo | null = null;

    constructor(guid: string, appUrl: string, apiUrl: string, language: string | null) {
        this.guid = guid;
        this.appUrl = appUrl ?? '';
        this.apiUrl = apiUrl ?? '';
        this.assistant = null;
        this.language = language ?? '';
        
        this.clientInfo = {
            clientID: this.getCookie('csl_clientid') || localStorage.getItem('csl_clientid'),
            sessionID: this.getCookie('csl_sessionid') || localStorage.getItem('csl_sessionid'),
        };

        if (!this.appUrl.endsWith('/'))
            this.appUrl += '/';

        if (!this.apiUrl.endsWith('/'))
            this.apiUrl += '/';
    }

    public open = async() => {
        this.toggleWidget(true);
    };

    public close = async() => {
        this.toggleWidget(false);
    };

    public init = async (openDelay: number) => {
        var valid = this.validate();

        if (!valid) {
            console.warn('Only one instance of CSL AI can be made!')
            return;
        }

        this.insertStyleSheet();
        this.setupMessageListener();
        this.createToggleButton();

        const self = this;
        if (openDelay > 0) {
            setTimeout(function() {
                self.toggleWidget(true);
                //(self.chatButtonFrame?.contentWindow as any).postMessage({ type: 'CSL_OPEN', isOpen: true }, '*');
            }, openDelay);
        }

    };

    private validate = () => {
        const exists = window.document.querySelector('.csl-chatbutton');

        return exists == null;
    }

    private createChatWindow = () => {
        try {
            if (this.chatWindow != null) {
                // already created
                return;
            }

            const windowElement = document.createElement('div');
            windowElement.className = 'csl-chatwidget';

            if (!this.assistant?.chatIconVisible) {
                windowElement.classList.add('csl-chatwidget-hidden');
            } else {
                windowElement.classList.remove('csl-chatwidget-hidden');
            }

            // Add the button position, default right
            windowElement.classList.add(this.assistant?.iconPosition == 1 ? 'left' : 'right');
            windowElement.classList.add('hide');

            // Create the frame
            const frame = document.createElement('iframe');
            frame.id = 'csl-window';
            frame.style.border = 'none';
            frame.style.flexGrow ='1';
            frame.style.background = 'none';
            frame.style.borderRadius = 'inherit';

            var frameSrc = this.appUrl + 'assistant/' + encodeURIComponent(this.guid) + '?widget=1';
            if (this.language)
                frameSrc += '&lang=' + encodeURIComponent(this.language);

            // Set clientInfo
            frameSrc = frameSrc + '&cid=' + encodeURIComponent(this.clientInfo?.clientID ?? '') + '&sid=' + encodeURIComponent(this.clientInfo?.sessionID ?? '');

            frame.src = frameSrc;
            
            windowElement.appendChild(frame);

            document.body.appendChild(windowElement);

            this.chatWindow = windowElement;  // Store reference to chat window
            this.chatWindowFrame = frame;
        } catch (error: any) {
            console.error(errorPrefix + error.message);
        }
    };

    private createToggleButton = () => {
        try {
            const button = document.createElement('div');
            button.className = 'csl-chatbutton';

            button.classList.add('right');
            button.classList.add('csl-chatbutton-hidden');

            // Create the frame
            const frame = document.createElement('iframe');

            frame.id = 'csl-button';
            frame.style.border = 'none';
            frame.style.background = 'none';
            frame.style.height = '100%';
            frame.style.width = '100%';

            var frameSrc = this.appUrl + 'button/' + encodeURIComponent(this.guid) + '?1=1';
            if (this.language) {
                frameSrc += '&lang=' + encodeURIComponent(this.language);
            }

            // Set ClientID
            frameSrc += '&cid=' + encodeURIComponent(this.clientInfo?.clientID ?? '') + '&sid=' + encodeURIComponent(this.clientInfo?.sessionID ?? '');

            frame.src = frameSrc;

            // Add to the body
            button.appendChild(frame);
            document.body.appendChild(button);
            
            this.chatButton = button;
            this.chatButtonFrame = frame;
        } catch (error: any) {
            console.error(errorPrefix + error.message);
        }
    };

    private addSuggestedItems = (items?: SuggestedItem[], theme?: ThemeSettings) => {
        const suggestedItems = document.createElement('div');
        suggestedItems.className = 'csl-suggesteditems';
        suggestedItems.classList.add(this.assistant?.iconPosition == 1 ? 'left' : 'right');

        if (!items || items.length === 0) {
            return;
        }

        const colorMode = sessionStorage.getItem('csl-color-mode') ?? 'light';

        let bgColor;
        let color;

        if (colorMode == 'dark') {
            bgColor = theme?.chatIcon?.darkColor || '#f1f1f1';
            color = theme?.chatIcon?.darkBgColor || '#fff';
        }
        else {
            bgColor = theme?.chatIcon?.color || '#f1f1f1';
            color = theme?.chatIcon?.bgColor || '#fff';
        }

        items.forEach((item, index) => {
            const itemWrapper = document.createElement('div');
            itemWrapper.className = 'csl-suggesteditem-wrapper';

            const button = document.createElement('button');
            button.className = 'csl-suggesteditem';
            button.innerHTML = item.label ?? '';
            button.style.animationDelay = `${index * 0.3}s`;

            button.style.backgroundColor = bgColor;
            button.style.color = color;

            button.addEventListener('click', () => {
                this.handleSuggestedItem(item);
            });
    
            itemWrapper.appendChild(button);
            suggestedItems.appendChild(itemWrapper);
        });
    
        // Add a single close button for the entire wrapper
        const closeButton = document.createElement('span');
        closeButton.className = 'csl-close-icon';
        closeButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="12.5" height="12.5"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>';
    
        closeButton.addEventListener('click', () => {
            suggestedItems.remove();
        });
    
        suggestedItems.appendChild(closeButton);
    
        setTimeout(() => {
            if (!this.isOpen) {
                document.body.appendChild(suggestedItems); // Append only if the widget is closed
            }
        }, 5000);
    };    
    
    
    private handleSuggestedItem = (item: SuggestedItem) => {
        this.toggleWidget(true);
        const iframe = this.chatWindowFrame as HTMLIFrameElement;
    
        try {
            setTimeout(() => {
                iframe.contentWindow?.postMessage(
                    {
                        type: 'SEND_MESSAGE',
                        prompt: item.prompt,
                    },
                    '*'
                );
            }, 1000);
        } catch (error) {
            console.error('Error posting message:', error);
        }
    };
    
    
    private setCookie = (name: string, value: string) => {
        const date = new Date();
    
        // Set it expire in 7 days
        date.setTime(date.getTime() + (7 * 24 * 60 * 60 * 1000));
    
        // Set it
        document.cookie = name+"="+value+"; expires="+date.toUTCString()+"; path=/; SameSite=Lax";
    };

    private getCookie = (name: string) => {
        const value = "; " + document.cookie;
        const parts = value.split("; " + name + "=");
        
        if (parts && parts.length == 2) {
            return parts.pop()?.split(";")?.shift() ?? '';
        }

        return null;
    }

    private deleteCookie = (name: string) => {
        const date = new Date();

        // Set it expire in -1 days
        date.setTime(date.getTime() + (-1 * 24 * 60 * 60 * 1000));
    
        // Set it
        document.cookie = name+"=; expires="+date.toUTCString()+"; path=/; SameSite=Lax";
    }

    private setupMessageListener = () => {
        window.addEventListener('message', (event) => {
            if (event.data.type === 'CSL_LOADED') {
                const assistantData = JSON.parse(event?.data.assistant) as ExternalAssistantSettings;
                const assistantTheme = JSON.parse(event?.data.theme) as ThemeSettings;

                this.assistant = assistantData;

                if (this.assistant.chatIconVisible) {
                    this.chatButton?.classList.remove('csl-chatbutton-hidden');
                }

                if (this.assistant.iconPosition == 1) {
                    this.chatButton?.classList.remove('right');
                    this.chatButton?.classList.add('left');
                }

                if (event?.data?.clientid) {
                    this.setCookie('csl_clientid', event?.data?.clientid);
                    sessionStorage.setItem('csl_clientid', event?.data?.clientid);
                }
    
                if (event?.data?.sessionid) {
                    const storedSessionID = sessionStorage.getItem('csl_sessionid');
                    this.setCookie('csl_sessionid', event?.data?.sessionid);
                    sessionStorage.setItem('csl_sessionid', event?.data?.sessionid);
    
                    const animationPlayedForSession = sessionStorage.getItem('suggesteditems-shown') === 'true';

                    if (storedSessionID !== event.data.sessionid) {    
                        sessionStorage.setItem('suggesteditems-shown', 'false');
                        const conversationExists = localStorage.getItem('conversationGuid-' + (this.guid ?? ''));
        
                        if (!conversationExists && assistantData.suggestedItemsPopup && assistantData.chatIconVisible) {
                            const suggestedItems = JSON.parse(event?.data.suggestedItems) as SuggestedItem[];
                            if ((suggestedItems?.length ?? 0) > 0) {
                                this.addSuggestedItems(suggestedItems, assistantTheme);
                            }
                        } 
                    }
    
                    if (!animationPlayedForSession) {
                        sessionStorage.setItem('suggesteditems-shown', 'true');
                    }
                }
    
                if (this.chatButton) {
                    this.chatButton?.classList.add('csl-chatbutton-loaded');
                }
            }
            else if (event.data.type === 'CSL_ERROR') {
                if (this.chatButton) {
                    this.chatButton?.classList.remove('csl-chatbutton-loaded');
                }
                // button failed to get assistant. Hide button.
                console.error('Consulink AI widget removed. Invalid or not found Assistant ID.');
    
                try {
                    // remove the chatbutton and chatwindow from DOM
                    if (this.chatButton)
                        this.chatButton.remove();
    
                    if (this.chatWindow)
                        this.chatWindow.remove();
                }
                catch(e) {
                    // something went wrong
                }
            }
            else if (event.data.type === 'CSL_OPEN') {
                this.toggleWidget(event.data.isOpen);
            }
            else if (event.data.type === 'CSL_OPEN_EXTERNAL') {
                var extUrl = this.appUrl + 'assistant/' + encodeURIComponent(this.guid);
                
                if (this.language) {
                    extUrl += '?lang=' + encodeURIComponent(this.language);
                }
    
                // Set clientInfo
                extUrl = extUrl + '&cid=' + encodeURIComponent(this.clientInfo?.clientID ?? '') + '&sid=' + encodeURIComponent(this.clientInfo?.sessionID ?? '');

                if (event.data.guid) {
                    extUrl = extUrl + '&conv=' + encodeURIComponent(event.data.guid);
                }
                
                // Close the widget
                this.close();
                
                // Open ext window
                this.openNewWindow(extUrl);

                this.isOpen = true;
                (this.chatButtonFrame?.contentWindow as any)?.postMessage({ type: 'CSL_OPEN', isOpen: this.isOpen }, '*');

                // Completely delete the suggested items when widget is opened
                const suggestedItems = document.querySelector('.csl-suggesteditems');

                if (suggestedItems) {
                    suggestedItems.remove();
                }
            }
            else if (event.data.type === 'CSL_EXPAND') {
                this.expandWidget(event.data.isExpand);
            }
            else if (event.data.type === 'CSL_RESIZE') {
                (this.chatWindowFrame?.contentWindow as any)?.postMessage({ type: 'CSL_RESIZE', size: window.innerWidth }, '*');
            }
        });

        window.addEventListener('resize', () => {
            (this.chatWindowFrame?.contentWindow as any)?.postMessage({ type: 'CSL_RESIZE', size: window.innerWidth }, '*');
        });
    };

    private openNewWindow = (url: string) => {
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
    
        let width = 400, height = 800, left = 0, top = 0;
    
        if (this.chatWindowFrame) {
            const rect = this.chatWindowFrame.getBoundingClientRect();
            width = rect.width;
            height = rect.height;
            left = rect.left + window.screenX; // Adjust for window position
            top = rect.top + window.screenY + 50;   // Adjust for window position
        } else {
            width = 400;
            height = 800;
            left = this.assistant?.iconPosition === 1 ? 0 : screenWidth - width;
            top = screenHeight - height;
        }
    
        this.extWindow = window.open(url, "_blank", `width=${width},height=${height},left=${left},top=${top}`);
    
        if (this.extWindow) {
            this.extWindow.onload = () => {
                try {
                    this.extWindow!.addEventListener('beforeunload', () => {
                        this.extWindow = null;
                        this.isOpen = false;
                        (this.chatButtonFrame?.contentWindow as any)?.postMessage({ type: 'CSL_OPEN', isOpen: this.isOpen }, '*');
                    });
                } catch (error: any) {
                    console.error('Error on opening ext. window: ', error?.toString());
                }
            };
        }
    };

    private expandWidget = (isExpand: boolean) => {
        this.isExpanded = isExpand;
        
        if (this.chatWindow) {
            if (this.isExpanded) {
                this.chatWindow.classList.add('expand');
            } else {
                this.chatWindow.classList.remove('expand');
            }
        }
    }
    
    private toggleWidget = (isOpen: boolean) => {
        this.createChatWindow();

        this.isOpen = isOpen;
        
        if (this.extWindow) {
            if (!this.isOpen) {
                window.focus();
                this.extWindow.close();
                this.extWindow = null;
            }
        } 
        else if (this.chatWindow) {
            if (this.isOpen) {
                this.chatWindow.classList.remove('hide');
            } else {
                this.chatWindow.classList.add('hide');
            }
        }
    
        // Completely delete the suggested items when widget is opened
        const suggestedItems = document.querySelector('.csl-suggesteditems');
        if (suggestedItems) {
            if (this.isOpen) {
                suggestedItems.remove(); // Remove suggested items from DOM when widget is opened
            } else {
                suggestedItems.classList.remove('hide'); // Otherwise, just remove the 'hide' class
            }
        }
    
        (this.chatButtonFrame?.contentWindow as any)?.postMessage({ type: 'CSL_OPEN', isOpen: this.isOpen }, '*');
    };
    

    private insertStyleSheet = () => {
        const style = document.createElement('style');
    
        style.innerHTML = `
            .csl-chatbutton {
                opacity: 0;
                position: fixed;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 9999;
                width: 48px;
                max-width: 48px;
                height: 48px;
                max-height: 48px;
                padding: 0;
                margin: 0;
                bottom: 20px;
                border-radius: 50%;
                transition: transform 167ms cubic-bezier(0.33, 0.00, 0.00, 1.00);
                cursor: pointer;
                box-sizing: border-box;
                overflow: hidden;
                background: none;
                filter: drop-shadow(rgba(0, 0, 0, 0.06) 0px 1px 6px) drop-shadow(rgba(0, 0, 0, 0.16) 0px 2px 32px);
            }

            .csl-chatbutton.csl-chatbutton-loaded {
                opacity: 1;
            }


            .csl-chatbutton.csl-chatbutton-hidden {
                display: none !important;
            }
    
            .csl-chatbutton.left {
                left: 20px;
            }
    
            .csl-chatbutton.right {
                right: 20px;
            }

            .csl-chatbutton.csl-chatbutton-hidden {
                display: hidden;
            }
    
            .csl-chatbutton:hover {
                transform: scale(1.1);
            }
    
            .csl-chatbutton:active {
                transform: scale(0.8);
            }

            .csl-suggesteditems {
                position: fixed;
                z-index: 9999;
                min-width: fit-content;
                max-width: 500px;
                overflow: hidden;
                bottom: 60px;
                padding: 10px;
                display: flex;
                flex-direction: column-reverse;
                height: auto;
            }

            .csl-suggesteditems.left {
                left: 5px;
            }

            .csl-suggesteditems.right {
                right: 5px;
            }
            
            .csl-suggesteditems.left > .csl-suggesteditem-wrapper > .csl-suggesteditem {
                margin-left: 3px;
                margin-right: auto;
            }
            
            .csl-suggesteditems.right > .csl-suggesteditem-wrapper > .csl-suggesteditem {
                margin-left: auto;
                margin-right: 3px;
            }

            @media (max-width: 768px) {
                .csl-suggesteditems {
                    max-width: 350px;
                }
            }

            .csl-suggesteditem {
                display: block;
                margin: 5px 0;
                padding: 11px;
                background-color: #fff;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                border-radius: 20px 20px 4px 20px;
                border: 1px solid #ddd;
                cursor: pointer;
                text-align: center;
                opacity: 0;
                animation: slideInGrowFromBottomRight 0.6s ease-out forwards;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                text-align: left;
            }

            @media (max-width: 768px) {
                .csl-suggesteditem {
                    font-size: 11px;
                }
            }

            @keyframes slideInGrowFromBottomRight {
                0% {
                    transform: translate(50%, 50%) scale(0); /* Start off-screen at the bottom-right corner */
                    opacity: 0;
                }
                100% {
                    transform: translate(0, 0) scale(1); /* End at normal position with full size */
                    opacity: 1;
                }
            }

            .csl-suggesteditem-wrapper:hover {
                transform: scale(1);
            }

            .csl-suggesteditem-wrapper {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: space-between;
                max-height: fit-content;
                margin: 0 0 5px;
                overflow: hidden;
                transition: transform 0.3s ease;
                transform: scale(0.97);
            }

            .csl-close-icon {
                position: absolute;
                right: 0px;
                top: 0px;
                color: #000000;
                cursor: pointer;
                background-color: #e4e4e7;
                border-radius: 25px;
                width: 20px;
                height: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                visibility: hidden;
                font-weight: bold;
            }

            .csl-suggesteditems:hover .csl-close-icon {
                visibility: visible;
            }

            @media (max-width: 768px) {
                .csl-close-icon {
                    visibility: visible !important;
                }
            }

            .csl-chatwidget {
                position: fixed;
                display: flex;
                flex-direction: column;
                z-index: 9999;
                height: min(704px, 100% - 104px);
                min-height: 80px;
                width: 400px;
                max-height: 704px;
                padding: 0;
                margin: 0;
                bottom: 84px;
                border-radius: 16px;
                transition: width 200ms ease 0s, height 200ms ease 0s, max-height 200ms ease 0s, transform 300ms cubic-bezier(0, 1.2, 1, 1) 0s, opacity 83ms ease-out 0s;
                opacity: 1;
                filter: drop-shadow(rgba(0, 0, 0, 0.06) 0px 1px 6px) drop-shadow(rgba(0, 0, 0, 0.16) 0px 2px 32px);
            }
    
            .csl-chatwidget.expand {
                height: calc(100vh - 104px) !important;
                width: 800px;
                max-height: 100vh !important;
            }

            .csl-chatwidget.expand.left {
                transform-origin: left bottom;
            }

            .csl-chatwidget.expand.right {
                transform-origin: right bottom;
            }
    
            .csl-chatwidget.left {
                left: 20px;
                transform-origin: left bottom;
            }
    
            .csl-chatwidget.right {
                right: 20px;
                transform-origin: right bottom;
            }

            .csl-chatwidget-hidden {
                max-height: unset !important;
                bottom: 25px !important;
            }
    
            .csl-chatwidget.hide {
                opacity: 0 !important;
                transform: scale(0);
            }
    
            /* Mobile styles */
            @media (max-width: 768px) {
                .csl-chatwidget {
                    width: 100% !important;
                    height: 100% !important;
                    max-width: 100% !important;
                    max-height: 100% !important;
                    bottom: 0 !important;
                    border-radius: 0;
                }

                .csl-chatwidget.expand {
                    height: 100% !important;
                    max-height: 100% !important;
                }
    
                .csl-chatwidget.left {
                    left: 0px !important;
                }
        
                .csl-chatwidget.right {
                    right: 0px !important;
                }
    
                .csl-chatbutton.left {
                    left: 12.5px !important;
                }
        
                .csl-chatbutton.right {
                    right: 12.5px !important;
                }
        
                .csl-chatbutton {
                    bottom: 12.5px !important;
                }
        
                .csl-suggesteditems {
                    padding: 0px !important;
                    padding-top: 20px !important;
                }
        
                .csl-suggesteditem {
                    margin-top: 0px !important;
                    margin-bottom: 0px !important;
                }

                .csl-close-icon {
                    top: 0px;
                }
            }
        `;
    
        document.head.appendChild(style);
    };
}

const initializeWidget = () => {
    if (!document || !document.currentScript) {

        setTimeout(() => {
            initializeWidget();
        }, 2000);

        return;
    }

    const guid = document.currentScript?.getAttribute('assistant-guid');
    let autoOpen : number = 0;
    
    if (document.currentScript?.getAttribute('open') && parseInt(document.currentScript.getAttribute('open') ?? '') > 0) {
        autoOpen = parseInt(document.currentScript.getAttribute('open') ?? '');
    }

    let lang = "";
    if (document.currentScript?.getAttribute('lang')) {
        lang = document.currentScript?.getAttribute('lang') ?? ''; 
    }

    let appUrl = 'https://chat.consulink.ai/';

    try {
        const jsFileUrl = (document.currentScript as any)?.src;
        const jsUrl = new URL(jsFileUrl);

        if (jsUrl.href.indexOf('assets/js/') > -1) {
            appUrl = jsUrl.href.split('assets/js/')[0];
        } else {
            appUrl = jsUrl.origin;
        }
    }
    catch(e) {
        // do nothing
        console.error(errorPrefix, e);
    }

    let apiUrl : string = 'https://api.consulink.ai/'; 
    if (document.currentScript?.getAttribute('api-url')) {
        apiUrl = document.currentScript?.getAttribute('api-url') ?? ''; 
    }

    const $widget: ICslWidget = new CslWidget(guid ?? '', appUrl, apiUrl, lang);
    $window.CslWidget = $widget;

    if ($widget) {
        $window['cslwidget'] = $widget;
        $widget.init(autoOpen);
    }
};

initializeWidget();